import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './placeDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import { CREATE_PLACE, UPDATE_PLACE } from '@/modules/API/daycare/daycareHandler'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import { useTranslation } from 'react-i18next'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface PlaceDialogProps {
    daycareId: number
    isDialogOpen: boolean
    create: boolean
    handleCloseDialog: () => void
    // ADD
    addOnePlace?: (value: OccasionalSlotsPlaces) => void
    // EDIT
    place?: OccasionalSlotsPlaces
    updateOnePlace?: (value: OccasionalSlotsPlaces) => void
}

const PlaceDialog: FunctionComponent<PlaceDialogProps> = ({
    daycareId,
    isDialogOpen,
    create,
    handleCloseDialog,
    addOnePlace,
    place,
    updateOnePlace
}: PlaceDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [placeName, setPlaceName] = useState<string>(place?.name || '')
    const [placeAddress, setPlaceAddress] = useState<string>(place?.address || '')

    const [createOnePlace] = useMutation(CREATE_PLACE, {
        variables: {
            daycareId: daycareId,
            name: placeName,
            address: placeAddress
        },
        onCompleted: ({ createOccasionalSlotsPlaces }) => {
            if (addOnePlace) {
                addOnePlace({
                    id: createOccasionalSlotsPlaces,
                    name: placeName,
                    address: placeAddress
                })
            }

            setPlaceName('')
            setPlaceAddress('')

            showNotificationSuccess(t('placeAdded'))

            handleCloseDialog()
        },
        onError: () => {
            showNotificationError(t('errorCreatingPlace'))
        }
    })

    const [updatePlace] = useMutation(UPDATE_PLACE, {
        variables: {
            daycareId: daycareId,
            placeId: Number(place?.id) || 0,
            name: placeName,
            address: placeAddress
        },
        onCompleted: () => {
            if (updateOnePlace) {
                updateOnePlace({
                    id: place?.id || 0,
                    name: placeName,
                    address: placeAddress
                })
            }

            showNotificationSuccess(t('placeUpdated'))

            handleCloseDialog()
        },
        onError: () => {
            showNotificationError(t('errorUpdatingPlace'))
        }
    })

    const disabled: boolean = placeName === '' || placeAddress === ''

    return (
        <Dialog open={isDialogOpen} handleClose={handleCloseDialog} className={styles.Dialog}>
            <DialogHeader title={create ? t('createPlace') : t('editPlace')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <UserInput
                            id='newPlaceName'
                            type='text'
                            label={t('name')}
                            value={placeName}
                            setValue={setPlaceName}
                            fullWidth={true}
                            className={styles.UserInput}
                        />
                        <UserInput
                            id='newPlaceAddress'
                            type='text'
                            label={t('address')}
                            value={placeAddress}
                            setValue={setPlaceAddress}
                            fullWidth={true}
                            className={styles.UserInput}
                        />
                    </div>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleCloseDialog} className={styles.Button} />
                    <ButtonTextIcon
                        type={create ? 'add' : 'update'}
                        onClick={create ? createOnePlace : updatePlace}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default PlaceDialog
